import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';

/** Brand colors for charts (mirrors tailwind primary-600/500 and navy-900). */
export const CHART_COLORS = {
  primary: '#1a8376',
  primaryLight: '#28a091',
  navy: '#1e3a5f',
  grid: '#e2e8f0',
  axis: '#64748b',
  muted: '#94a3b8',
} as const;

/** Small categorical palette: brand first, then hues distinguishable from each other. */
export const CATEGORICAL_COLORS = [
  '#1a8376', // teal
  '#1e3a5f', // navy
  '#d97706', // amber
  '#7c3aed', // violet
  '#e11d48', // rose
  '#0ea5e9', // sky
  '#64748b', // slate
];

/** Prescription status colors, matching StatusBadge dots. */
export const STATUS_COLORS: Record<string, string> = {
  ACTIVE: '#28a091',
  PARTIALLY_FULFILLED: '#f97316',
  FULFILLED: '#22c55e',
  CANCELLED: '#ef4444',
  EXPIRED: '#f59e0b',
  DRAFT: '#94a3b8',
};

/** Normalizes "PARTIALLY FULFILLED" / "partially_fulfilled" to a status key, if it is one. */
export function toStatusKey(name: string): string | null {
  const key = name.trim().toUpperCase().replace(/[\s-]+/g, '_');
  return key in STATUS_COLORS ? key : null;
}

export const axisTick = { fontSize: 12, fill: CHART_COLORS.axis };

export const tooltipProps = {
  contentStyle: {
    backgroundColor: '#ffffff',
    border: `1px solid ${CHART_COLORS.grid}`,
    borderRadius: 8,
    boxShadow: '0 4px 12px -2px rgb(15 23 42 / 0.08), 0 2px 4px -2px rgb(15 23 42 / 0.05)',
    fontSize: 12,
    padding: '8px 12px',
  } as CSSProperties,
  labelStyle: { color: '#0f172a', fontWeight: 600, marginBottom: 4 } as CSSProperties,
  itemStyle: { color: '#334155', padding: 0 } as CSSProperties,
  cursor: { fill: 'rgb(148 163 184 / 0.12)' },
};

/** Locale used for number/date formatting, following the active UI language. */
export function localeFor(language: string | undefined): string {
  return language?.startsWith('ar') ? 'ar-DZ' : 'fr-DZ';
}

/** Chart helpers bound to the current language: locale, RTL flag, number formatter. */
export function useChartLocale() {
  const { i18n } = useTranslation();
  const locale = localeFor(i18n.language);
  const isRtl = i18n.dir(i18n.language) === 'rtl';
  const formatNumber = (value: unknown) =>
    typeof value === 'number' ? value.toLocaleString(locale) : String(value ?? '');
  const direction = isRtl ? 'rtl' : 'ltr';
  // Charts render inside a dir="ltr" box (axes are mirrored explicitly), so HTML
  // overlays (tooltip, legend) get their direction back here.
  const tooltip = {
    ...tooltipProps,
    contentStyle: { ...tooltipProps.contentStyle, direction, textAlign: 'start' } as CSSProperties,
  };
  const legendStyle: CSSProperties = { fontSize: 12, color: CHART_COLORS.axis, paddingTop: 8, direction };
  return { locale, isRtl, formatNumber, tooltip, legendStyle };
}

export type PeriodGrouping = 'day' | 'week' | 'month';

/**
 * Formats API period keys for display: YYYY-MM-DD → "12 oct.", and YYYY-MM → "oct. 2026"
 * when grouping is 'month' (week keys share the YYYY-NN shape, so months are opt-in).
 * Other values are returned unchanged.
 */
export function formatPeriod(
  value: unknown,
  locale: string,
  long = false,
  grouping?: PeriodGrouping
): string {
  const str = String(value ?? '');
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString(
      locale,
      long ? { day: 'numeric', month: 'long', year: 'numeric' } : { day: 'numeric', month: 'short' }
    );
  }
  m = grouping === 'month' ? /^(\d{4})-(\d{2})$/.exec(str) : null;
  if (m && Number(m[2]) >= 1 && Number(m[2]) <= 12) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
    return d.toLocaleDateString(locale, { month: long ? 'long' : 'short', year: 'numeric' });
  }
  return str;
}
